<template>
  <v-container id="wccm-calendar">

    <v-img 
      lazy-src="/assets/img/calendarCover/lazy/bg-cover.png"
      :src="coverImage"
      position="center"
    >
      <div class="fill-height bottom-gradient d-flex align-end justify-end">      
        <component :is="coverText"></component>
      </div>
    </v-img> 

    <v-row class="mb-10">
      <v-col class="pt-0 pb-0">

        <v-sheet tile>

          <v-toolbar tile class="calendar-toolbar">

            <v-toolbar-title class="headline font-weight-bold monthTitle">{{ title }}</v-toolbar-title>

            <v-spacer></v-spacer>

            <div class="break-calendar-toolbar hidden-sm-and-up"></div>

            <v-btn data-html2canvas-ignore="true" color="#6c7c69" @click="prev" :disabled="!hasPrev">
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn data-html2canvas-ignore="true" color="#6c7c69" class="ml-3" @click="next" :disabled="!hasNext">
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>

            <v-spacer class="hidden-sm-and-up"></v-spacer>

            <v-btn 
              data-html2canvas-ignore="true"
              outlined
              class="ml-3 hidden-sm-and-down" 
              @click="setToday">
                Today
            </v-btn>   

            <v-menu>
              <template v-slot:activator="{ on }">
                <v-btn data-html2canvas-ignore="true" outlined class="ml-3 hidden-sm-and-down" v-on="on">
                  <span>{{ containerClassToLabel[containerClass] }}</span>
                  <v-icon right>mdi-menu-down</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="containerClass = 'v-calendar-list'">
                  <v-list-item-title>List</v-list-item-title>
                </v-list-item>
                <v-list-item @click="containerClass = 'v-calendar-grid'">
                  <v-list-item-title>Grid</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <div v-if="isAuthenticated">
              <v-btn data-html2canvas-ignore="true" class="ml-3" color="#6c7c69" dark @click.stop="setDialogAddEvent">
                <v-icon>mdi-calendar-edit</v-icon>
                <span class="ml-2 hidden-sm-and-down">New Event</span>
              </v-btn>
            </div>

            <v-btn
                data-html2canvas-ignore="true"  
                outlined
                class="ml-3 hidden-sm-and-down"
                @click="printPage"
            >
                <v-icon left>mdi-printer</v-icon>
                PDF
            </v-btn>     

          </v-toolbar>
        </v-sheet>

        <!-- DIALOG GENERATING PDF start --> 
        <v-dialog transition="fade-transition" v-model="dialogGeneratingPdf" persistent max-width="500">
          <v-card class="pa-5">
            <v-card-title>
              Your PDF is being generated
            </v-card-title>
            <v-card-text>
              <p class="mb-0 subtitle-1 font-weight-medium">Please wait, it may take some time</p>
            </v-card-text>
          </v-card>
        </v-dialog>
        <!-- DIALOG GENERATING PDF end -->

        <!-- DIALOG NOT LOGGED start --> 
        <v-dialog transition="fade-transition" v-model="dialogNotLogged" persistent max-width="500">
          <v-card class="pa-5">
            <v-card-title>
              You must be logged to add Event
            </v-card-title>
            <v-card-text>
              <p class="mb-0 subtitle-1 font-weight-medium">Want to make the WCCM Calendar <i>your</i> Calendar</p>
              <p><router-link to="/sign-up">Create a free account</router-link> and you'll be able to enter your own event</p>
              <p>Already have a WCCM Calendar account? Go to <router-link to="/login">login</router-link>.</p>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn 
                class="ml-2"
                @click.stop="unSetDialogNotLogged"
              >
                Close
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <!-- DIALOG NOT LOGGED end -->

        <!-- DIALOG ADD EVENT start -->  
        <v-dialog transition="fade-transition" v-model="dialogAddEvent" persistent max-width="500">
          <v-card class="pa-3">
            <v-card-text>
              <v-form
                ref="formAddEvent"
                v-model="validFormAddEvent" 
              >
                <v-text-field 
                  class="d-none"
                  v-model="newEvent.userId"
                  type="hidden"
                ></v-text-field>
                <v-row>
                  <v-col
                    cols="12"
                    md="9"
                  >
                    <v-text-field 
                      v-model="newEvent.name"
                      label="Event Name*"
                      prepend-icon="mdi-calendar-blank-outline"
                      :rules="eventNameRules"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    md="3"
                  >
                    <v-menu
                      ref="eventColorMenuAdd"
                      v-model="eventColorMenuAdd"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      min-width="290px"
                      :nudge-top="20"
                      :nudge-right="33"
                      offset-y
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-model="newEvent.color"
                          label="Color"
                          prepend-icon="mdi-format-color-fill"
                          readonly
                          v-on="on"
                          class="input-color-picker"
                          :background-color="newEvent.color"
                        >
                        </v-text-field>
                      </template>
                      <v-color-picker
                        v-model="newEvent.color"
                        @input="eventColorMenuAdd = false"
                        hide-inputs
                        hide-mode-switch
                        hide-canvas
                        show-swatches
                        :swatches="swatches"
                      ></v-color-picker>
                    </v-menu>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <v-menu
                      ref="eventStartMenuAdd"
                      v-model="eventStartMenuAdd"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      min-width="290px"
                      :nudge-top="20"
                      :nudge-right="33"
                      offset-y
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-model="newEvent.start"
                          label="Start*"
                          prepend-icon="mdi-calendar"
                          readonly
                          :rules="eventStartRules"
                          required
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="newEvent.start"
                        no-title
                        scrollable
                        @input="eventStartMenuAdd = false"
                        :min="minDate"
                        :max="maxDate"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <v-menu
                      ref="eventEndMenuAdd"
                      v-model="eventEndMenuAdd"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      min-width="290px"
                      :nudge-top="20"
                      :nudge-right="33"
                      offset-y
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          :disabled="!validFormAddEvent"
                          v-model="newEvent.end"
                          label="End"
                          prepend-icon="mdi-calendar"
                          readonly
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="newEvent.end"
                        no-title
                        scrollable
                        @input="eventEndMenuAdd = false"
                        :min="newEvent.start"
                        :max="maxDate"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                </v-row>
                <ckeditor 
                  :editor="editor" 
                  v-model="newEvent.details" 
                  :config="userTypeisAdmin ? editorConfigAdmin : editorConfig"
                ></ckeditor>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn 
                class="ml-2"
                @click.stop="unSetDialogAddEvent"
              >
                Close
              </v-btn>
              <v-btn 
                class="mr-4"
                :disabled="!validFormAddEvent"
                @click="addEvent"
                color="primary"
              >
                Add Event
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <!-- DIALOG ADD EVENT end -->  

        <!-- DIALOG UPDATE EVENT start -->  
        <v-dialog transition="fade-transition" v-model="dialogUpdateEvent" persistent max-width="500">
          <v-card class="pa-3">
            <v-card-text>
              <v-form
                ref="formUpdateEvent"
                v-model="validFormUpdateEvent" 
              >
                <v-text-field 
                  class="d-none"
                  value="fix for ckeditor problem"
                  type="hidden"
                ></v-text-field>
                <v-row>
                  <v-col
                    cols="12"
                    md="9"
                  >
                    <v-text-field 
                      v-model="selectedEvent.name"
                      label="Event Name*"
                      prepend-icon="mdi-calendar-blank-outline"
                      :rules="eventNameRules"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    md="3"
                  >
                    <v-menu
                      ref="eventColorMenuUpdate"
                      v-model="eventColorMenuUpdate"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      min-width="290px"
                      :nudge-top="20"
                      :nudge-right="33"
                      offset-y
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-model="selectedEvent.color"
                          label="Color"
                          prepend-icon="mdi-format-color-fill"
                          readonly
                          v-on="on"
                          class="input-color-picker"
                          :background-color="selectedEvent.color"
                        ></v-text-field>
                      </template>
                      <v-color-picker
                        v-model="selectedEvent.color"
                        @input="eventColorMenuUpdate = false"
                        hide-inputs
                        hide-mode-switch
                        hide-canvas
                        show-swatches
                        :swatches="swatches"
                      ></v-color-picker>
                    </v-menu>
                  </v-col>
                </v-row>                   
                <v-row>
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <v-menu
                      ref="eventStartMenuUpdate"
                      v-model="eventStartMenuUpdate"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      min-width="290px"
                      :nudge-top="20"
                      :nudge-right="33"
                      offset-y
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-model="selectedEvent.start"
                          label="Start*"
                          prepend-icon="mdi-calendar"
                          readonly
                          :rules="eventStartRules"
                          required
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="selectedEvent.start"
                        no-title
                        scrollable
                        @input="eventStartMenuUpdate = false"
                        :min="minDate"
                        :max="maxDate"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <v-menu
                      ref="eventEndMenuUpdate"
                      v-model="eventEndMenuUpdate"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      min-width="290px"
                      :nudge-top="20"
                      :nudge-right="33"
                      offset-y
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          :disabled="!validFormUpdateEvent"
                          v-model="selectedEvent.end"
                          label="End"
                          prepend-icon="mdi-calendar"
                          readonly
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="selectedEvent.end"
                        no-title
                        scrollable
                        @input="eventEndMenuUpdate = false"
                        :min="selectedEvent.start"
                        :max="maxDate"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <ckeditor 
                      :editor="editor" 
                      v-model="selectedEvent.details" 
                      :config="userTypeisAdmin ? editorConfigAdmin : editorConfig"
                      tag-name="textarea"
                    ></ckeditor>
                  </v-col>
                </v-row>                
              </v-form>           
            </v-card-text>
            <v-card-actions>
              <v-btn
                class="ml-4"
                color="red" 
                @click="deleteEvent(selectedEvent.id)">
                Delete
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn 
                :disabled="!validFormUpdateEvent"
                class="mr-4"
                @click="updateEvent(selectedEvent)"
                color="primary"
              >
                Update Event
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <!-- DIALOG UPDATE EVENT end -->  


        <v-sheet :class="containerClass">
          <v-calendar
            dark
            ref="calendar"
            v-model="focus"
            color="#6c7c69"
            :showMonthOnFirst="false"
            :events="events"
            :event-color="getEventColor"
            :event-more="false"
            :now="today"
            :type="type"
            @click:event="showEvent"
            @click:date="setDialogAddEvent"
            @change="updateRange"
          >
          </v-calendar>

          <v-menu
            v-model="selectedOpen"
            :close-on-content-click="false"
            :activator="selectedElement"
            min-width= "350"
          >
            <v-card flat>
              <v-toolbar :color="selectedEvent.color" dark>
                <v-toolbar-title v-html="selectedEvent.name"></v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn @click="selectedOpen = false" icon class="ml-2">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-toolbar>

              <v-card-text>
                <v-simple-table dark dense>
                  <tbody>
                    <tr>
                      <td v-if="selectedEvent.end">Event start</td>
                      <td v-else>Event time</td>
                      <td class="font-weight-black">{{selectedEvent.start}}</td>
                    </tr>
                    <tr v-if="selectedEvent.end">
                      <td>Event end</td>
                      <td class="font-weight-black">{{selectedEvent.end}}</td>
                    </tr>                    
                  </tbody>
                </v-simple-table>
                <div
                  v-html="selectedEvent.details" 
                  :class="selectedEvent.details ? 'pa-4 pb-0' : ''"
                ></div>
              </v-card-text>

              <v-card-actions v-if="isAuthenticated && selectedEvent.user == $store.state.user.user.uid">
                <v-spacer></v-spacer>
                <v-btn small @click.prevent="setDialogUpdateEvent(selectedEvent)">
                  Edit event
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>

        </v-sheet>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { db } from '@/firebase/'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

export default {
  data: () => ({
    today: new Date().toISOString().substr(0, 10),
    focus: new Date().toISOString().substr(0, 10),
    type: 'month',
    containerClass: 'v-calendar-list',
    containerClassToLabel: {
      'v-calendar-grid': 'Grid',
      'v-calendar-list': 'List',
    },
    start: null,
    end: null,
    currentYearMonth: null,
    minDate: "2019-12-01",
    maxDate: "2022-12-31",
    userTypeisAdmin: false,
    dialogNotLogged: false,
    dialogGeneratingPdf: false,
    editor: ClassicEditor,
    editorConfigAdmin: {
        link: { addTargetToExternalLinks: true },
        placeholder: 'Add details for your event.',
        toolbar: [ 'bold', 'italic', 'bulletedList', 'numberedList', '|', 'link' ]
    }, 
    editorConfig: {
        placeholder: 'Add details for your event.',
        toolbar: [ 'bold', 'italic', 'bulletedList', 'numberedList' ]
    }, 
    swatches: [
      ['#A38765', '#CB68A1', '#A47A92'],
      ['#834AA0', '#406EC4', '#B47391'],
      ['#4A817A', '#86717A', '#754D00'],
      ['#AE7E1B', '#AE6DAB', '#55414A'],
      ['#C24587', '#FF88C8', '#700041']
    ],
    newEvent: {
      userId: '',
      name: '',
      details: '',
      start: '',
      end: '',    
      color: '',
      img: ''
    },
    eventColorMenuAdd: false,
    eventStartMenuAdd: false,
    eventEndMenuAdd: false,
    eventColorMenuUpdate: false,
    eventStartMenuUpdate: false,
    eventEndMenuUpdate: false,    
    validFormAddEvent: false,
    validFormUpdateEvent: false,  
    eventNameRules: [
        v => !!v || 'Event Name is required'
    ],
    eventStartRules: [
        v => !!v || 'Event Start time is required'
    ],
    currentlyEditing: null,
    selectedEvent: {},
    selectedElement: null,
    selectedOpen: false,
    events: [],
    dialogAddEvent: false,
    dialogUpdateEvent: false
  }),
  mounted () {
    this.$refs.calendar.checkChange()
    this.getEvents()
  },
  computed: {
    hasPrev() {
      if (this.minDate.slice(0, -3) == this.currentYearMonth)
        return false
      else
        return true
    },
    hasNext() {
      if (this.maxDate.slice(0, -3) == this.currentYearMonth)
        return false
      else
        return true
    },
    isAuthenticated () {
      return this.$store.getters.isAuthenticated
    },  
    title () {
      const { start } = this
      if (!start) {
        return ''
      }
      const year = start.year
      const monthFormat = this.monthFormatter(start)
      
      return `${monthFormat} ${year}`
    },
    monthFormatter () {
      return this.$refs.calendar.getFormatter({
        timeZone: 'UTC', month: 'long', 
      })
    },
    coverImage () {
      if (!this.currentYearMonth) 
        return ''
      else
        return require('@/assets/img/calendarCover/'+ this.currentYearMonth +'.jpg')
    },
    coverText () {
      if (!this.currentYearMonth) 
        return ''
      else
        return () => import(`@/components/calendarCover/${this.currentYearMonth}.vue`);
    }
  },
  methods: {
    manipulateElementEvent(){
      const matchEventIdLabel = []
      Array.from(document.querySelectorAll('.v-event')).forEach((elementEvent) => {
        // define event class from data-event attribute
        var classEventId = 'v-event-'+elementEvent.getAttribute('data-event')
        // if element event has pl-1(old name label) define my label and
        // push element to array matchEventIdLabel
        if(elementEvent.querySelector('.pl-1')) {
          var nameEvent = elementEvent.querySelector('.pl-1').textContent
          matchEventIdLabel.push({'classEvent':classEventId, 'nameEvent':nameEvent})
        }
        //then add class to element event
        elementEvent.classList.add(classEventId);
      });

      // loop array and create name label for each class event div
      matchEventIdLabel.forEach(item => {
        Array.from(document.querySelectorAll('.'+item.classEvent)).forEach((el) => {
          if(el.querySelector('.v-event-name')){
            var removeEventName = el.querySelectorAll(".v-event-name")[0];
            removeEventName.parentNode.removeChild(removeEventName);              
          }
          var divNameEvent = document.createElement('div');
          divNameEvent.classList.add('v-event-name');
          divNameEvent.innerText = item.nameEvent;
          el.append(divNameEvent);
        })
      });
    },
    async getEvents () {
      const events = []
      let churchEvent = await db.collection('churchEvent').get()
      churchEvent.forEach(doc => {
        let appData = doc.data()
        appData.id = doc.id
        events.push(appData)
      })
      let publicEvent = await db.collection('publicEvent').get()
      publicEvent.forEach(doc => {
        let appData = doc.data()
        appData.id = doc.id
        events.push(appData)
      })
      if(this.$store.getters.isAuthenticated) {
        let privateEvent = await db.collection('privateEvent').where("user", "==", this.$store.state.user.user.uid).get()
        privateEvent.forEach(doc => {
          let appData = doc.data()
          appData.id = doc.id
          events.push(appData)
        })      
      }
      this.events = events
      await this.$nextTick(() => {
        this.manipulateElementEvent()
      })
    },
    setDialogNotLogged(){
      this.dialogNotLogged = true
    },
    unSetDialogNotLogged(){
      this.dialogNotLogged = false
    },    
    setDialogAddEvent({ date }) {
      this.clearFormAddEvent()
      if(this.$store.getters.isAuthenticated) {       
        this.newEvent.userId = this.$store.state.user.user.uid
        if (this.$store.state.user.user.email == 'wccmnews@gmail.com') {
          this.newEvent.color = '#A38765'
          this.userTypeisAdmin = true
        } else if (this.$store.state.user.user.email == 'leonardo.correa@gmail.com') {
          this.newEvent.color = '#CB68A1'
          this.userTypeisAdmin = true
        } else {
          this.newEvent.color = '#A47A92'
          this.userTypeisAdmin = false
        }
        if(date) {
          this.focus = date
          this.newEvent.start = date
        } else {
          this.newEvent.start = ''
        }
        this.dialogAddEvent = true
      }else{
        this.setDialogNotLogged()
      }
    },
    unSetDialogAddEvent () {
      this.$refs.formAddEvent.resetValidation()
      this.clearFormAddEvent()
      this.dialogAddEvent = false
    },
    clearFormAddEvent(){
      this.newEvent = {
        userId: '',
        name: '',
        details: '',
        start: '',
        end: '',    
        color: '',
        img: ''
      }
    },
    setDialogUpdateEvent(ev) {
      if (this.$store.state.user.user.email == 'wccmnews@gmail.com') {
        this.userTypeisAdmin = true
      } else if (this.$store.state.user.user.email == 'leonardo.correa@gmail.com') {
        this.userTypeisAdmin = true
      } else {
        this.userTypeisAdmin = false
      }
      this.currentlyEditing = ev.id
      this.selectedOpen = false
      this.dialogUpdateEvent = true
    },
    unSetDialogUpdateEvent() {
      //this.$refs.formUpdateEvent.resetValidation()
      this.currentlyEditing = null
      this.clearFormUpdateEvent()
      this.dialogUpdateEvent = false
    },
    clearFormUpdateEvent(){
      this.selectedEvent = {
        user: '',
        name: '',
        details: '',
        start: '',
        end: '',
        color: '',
        image: '',
        id: ''
      }
    },     
    getEventColor (event) {
      return event.color
    },
    setToday () {
      this.focus = this.today
    },
    prev () {
      this.$refs.calendar.prev()
    },
    next () { 
      this.$refs.calendar.next()
    },
    async addEvent () {
      if (this.$refs.formAddEvent.validate()) {
        let where = ''
        if (this.$store.state.user.user.email == 'wccmnews@gmail.com') {
          where = 'churchEvent'
        } else if (this.$store.state.user.user.email == 'leonardo.correa@gmail.com') {
          where = 'publicEvent'
        } else {
          where = 'privateEvent'
        }
        await db.collection(where).add({
          user: this.newEvent.userId,
          name: this.newEvent.name,
          details: this.newEvent.details,
          start: this.newEvent.start,
          end: this.newEvent.end,
          color: this.newEvent.color,
          image: this.newEvent.img
        })
        this.getEvents(),
        this.unSetDialogAddEvent()               
      }
    },
    async updateEvent (ev) {
      if (this.$refs.formUpdateEvent.validate()) {
        let where = ''
        if (this.$store.state.user.user.email == 'wccmnews@gmail.com') {
          where = 'churchEvent'
        } else if (this.$store.state.user.user.email == 'leonardo.correa@gmail.com') {
          where = 'publicEvent'
        } else {
          where = 'privateEvent'
        }
        await db.collection(where).doc(this.currentlyEditing).update({
          name: ev.name,
          details: ev.details,
          start: ev.start,
          end: ev.end,
          color: ev.color
        })
        this.getEvents(),
        this.unSetDialogUpdateEvent()      
      }
    },
    async deleteEvent (ev) {
      let where = ''
      if (this.$store.state.user.user.email == 'wccmnews@gmail.com') {
        where = 'churchEvent'
      } else if (this.$store.state.user.user.email == 'leonardo.correa@gmail.com') {
        where = 'publicEvent'
      } else {
        where = 'privateEvent'
      }      
      await db.collection(where).doc(ev).delete()
      this.dialogUpdateEvent = false
      this.getEvents()
    },
    showEvent ({ nativeEvent, event }) {
      const open = () => {
        this.selectedEvent = event
        this.selectedElement = nativeEvent.target
        setTimeout(() => this.selectedOpen = true, 10)
      }
      if (this.selectedOpen) {
        this.selectedOpen = false
        setTimeout(open, 10)
      } else {
        open()
      }
      nativeEvent.stopPropagation()
    },
    updateRange ({ start, end }) {
      this.start = start
      this.end = end
      this.currentYearMonth = start.year+'-'+start.month
      this.$nextTick(() => {
        this.manipulateElementEvent()
      })
    },
    printPage() {
      //alert('Not Available Yet')  
      var canvasElement = document.createElement('canvas');
      html2canvas(document.querySelector("#wccm-calendar"), { canvas: canvasElement }).then(function(canvas) {
        
        let imgWidth = 210; 
        let pageHeight = 295;  
        let imgHeight = canvas.height * imgWidth / canvas.width;
        let heightLeft = imgHeight;
        const imgData = canvas.toDataURL('image/png', 0.5);
        const doc = new jsPDF('p', 'mm');
        let position = 0;

        doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          doc.addPage();
          doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }
        doc.save('wccm-calendar.pdf');
        
      });
      
    }
  } 
}
</script>

<style lang="scss">

.input-color-picker {
  input {
    color: transparent!important;
    border-top: 8px solid #424242;
    cursor: pointer;
  }
}

.v-color-picker__controls {
  display: none;
}
.v-color-picker__swatches {
  padding-top: 15px;
}

.v-application .v-image {
   height: 650px;
  @media only screen and (max-width: 959px){
    height: 450px;
  }
  .v-image__image {  
    @media only screen and (max-width: 959px){
      opacity: .6;
    }     
  }
  .calendar-text {
    font-family: 'Patua One', cursive!important;
    color: #fff;
    text-shadow: 3px 3px #000;
    p {
      width: 100%;
      margin: 0;
    }
    @media only screen and (max-width: 959px){
      font-size: 1rem!important;
      text-shadow: 2px 2px #000!important;
      p {
        br {
          display: none;
        }
      }
    }  
  }
  .calendar-credit {
    background-color: rgba(0, 0, 0, 0.3);
    color: #999;
  } 
}

.theme--dark.v-card > .v-card__text {
  color: #fff
}

.monthTitle {
  text-transform: uppercase;
  color: #fff;
}

.bottom-gradient {
  background-image: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, transparent 200px);
}

.calendar-toolbar {
  .v-toolbar__content {
    height: auto!important;
    padding: 10px 16px;
    flex-wrap: wrap;
    .break-calendar-toolbar {
      flex-basis: 100%;
      height: 10px;
    }
  }
}

.v-calendar-grid {
  height: 1010px;
  margin-bottom: 20px;
  .v-calendar-monthly {
    border-left: #424242 1px solid;
    border-radius: 0!important;
  }
  .v-calendar-weekly__head {
    background-color: #000;
    font-weight: bold;
    .v-calendar-weekly__head-weekday {
      font-size: 14px;
      border-right: #424242 1px solid!important;
    }
  }
  .v-calendar-weekly__day {
    border-right: #424242 1px solid!important;
    border-bottom: #424242 1px solid!important;
    background-color: #CFCED0;
    overflow-y: auto!important;
    &::-webkit-scrollbar-track {
      margin: 66px 0 20px 0;
      -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.3);
    }
    &::-webkit-scrollbar {
      width: 3px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: #424242;
    }
    &.v-outside {
      opacity: .6;
      background-color: #999!important;
      .v-calendar-weekly__day-label {
        background-color: #999!important;
      }
    }
    .v-calendar-weekly__day-label {
      background-color: #CFCED0;
      position: sticky;
      top: 0;
      z-index: 2;
      width: 100%;
      margin: 0;
      .v-btn {
        color: #087c59!important;
        margin: 3px;
        .v-btn__content {
          font-weight: bold;
        }
      }
    }
    &.v-present {
      .v-calendar-weekly__day-label {
        .v-btn {
          color: #fff!important;
        }       
      }
    }
    .v-event {
      top: 0!important;
      left: 0!important;
      display: flex;
      align-items: center;
      width: auto!important;
      margin: 0 3px 3px 3px!important;
      border-radius: 5px!important;   
      .pl-1 {
        display: none!important;
      }
      .v-event-name {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;        
        padding:0 5px!important;
      }
    }    
  }
}

.v-calendar-list {
  height: auto;
  margin-bottom: 20px;
  .v-calendar-weekly__head {
    display:none
  }
  .v-calendar-weekly__week {
    display: block;
    height: auto;
    .v-calendar-weekly__day {
      display: flex;
      align-content: center;
      align-items: center;
      flex-wrap: wrap;
      border-right: none!important;
      width: 100%;
      min-height: 74px;
      background-color: #CFCED0;
      position: relative;
      padding: 3px 0 3px 74px; 
      &:nth-child(1) { background: #a6b0a5; .v-calendar-weekly__day-label .v-btn .v-btn__content::after{ content: "Sun" } }
      &:nth-child(2) { .v-calendar-weekly__day-label .v-btn .v-btn__content::after{ content: "Mon" } }
      &:nth-child(3) { .v-calendar-weekly__day-label .v-btn .v-btn__content::after{ content: "Tue" } }
      &:nth-child(4) { .v-calendar-weekly__day-label .v-btn .v-btn__content::after{ content: "Wed" } }
      &:nth-child(5) { .v-calendar-weekly__day-label .v-btn .v-btn__content::after{ content: "Thu" } }
      &:nth-child(6) { .v-calendar-weekly__day-label .v-btn .v-btn__content::after{ content: "Fri" } }
      &:nth-child(7) { .v-calendar-weekly__day-label .v-btn .v-btn__content::after{ content: "Sat" } } 
      .v-calendar-weekly__day-label {
        position: absolute;
        top: 0;
        left: 0;
        margin: 0;
        .v-btn {
          color: #087c59!important;
          height: 64px;
          width: 64px;
          margin: 5px;
          align-items: center;
          .v-btn__content {
            font-weight: bold;
            font-size: 1.5rem !important;
            &::after{
              font-size: .8rem;
              font-weight: normal;
              text-transform: uppercase;
            }
          }
        }  
      }
      &.v-present {
        .v-calendar-weekly__day-label {
          .v-btn {
            color: #fff!important;
          }       
        }
      }
      .v-event {
        display: flex;
        align-items: center;
        top: 0!important;
        left: 0!important;
        width: auto!important;
        max-width: 200px!important;
        height: 25px!important;
        margin: 3px 6px 3px 0!important;
        border-radius: 5px!important;
        .pl-1 {
          display: none!important;
        }
        .v-event-name {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          padding:0 5px!important;
        }
      }
            
    }
    .v-outside {
      display: none
    }
  }
}

/*CKEDITOR*/
.ck-editor {
  color: #000;
}
.ck-editor {
  overflow: hidden;
  a {
    color: #000!important
  }
}
.ck-editor__editable {
  overflow: hidden;
  width: 100%;
  min-height: 150px;
}
:root {

    /* Overrides the border radius setting in the theme. */
    --ck-border-radius: 4px;

    /* Overrides the default font size in the theme. */
    --ck-font-size-base: 14px;

    /* Helper variables to avoid duplication in the colors. */
    --ck-custom-background: hsl(270, 1%, 29%);
    --ck-custom-foreground: hsl(255, 3%, 18%);
    --ck-custom-border: hsl(300, 1%, 22%);
    --ck-custom-white: hsl(0, 0%, 100%);

    /* -- Overrides generic colors. ------------------------------------------------------------- */

    --ck-color-base-foreground: var(--ck-custom-background);
    --ck-color-focus-border: hsl(208, 90%, 62%);
    --ck-color-text: hsl(0, 0%, 98%);
    --ck-color-shadow-drop: hsla(0, 0%, 0%, 0.2);
    --ck-color-shadow-inner: hsla(0, 0%, 0%, 0.1);

    /* -- Overrides the default .ck-button class colors. ---------------------------------------- */

    --ck-color-button-default-background: var(--ck-custom-background);
    --ck-color-button-default-hover-background: hsl(270, 1%, 22%);
    --ck-color-button-default-active-background: hsl(270, 2%, 20%);
    --ck-color-button-default-active-shadow: hsl(270, 2%, 23%);
    --ck-color-button-default-disabled-background: var(--ck-custom-background);

    --ck-color-button-on-background: var(--ck-custom-foreground);
    --ck-color-button-on-hover-background: hsl(255, 4%, 16%);
    --ck-color-button-on-active-background: hsl(255, 4%, 14%);
    --ck-color-button-on-active-shadow: hsl(240, 3%, 19%);
    --ck-color-button-on-disabled-background: var(--ck-custom-foreground);

    --ck-color-button-action-background: hsl(168, 76%, 42%);
    --ck-color-button-action-hover-background: hsl(168, 76%, 38%);
    --ck-color-button-action-active-background: hsl(168, 76%, 36%);
    --ck-color-button-action-active-shadow: hsl(168, 75%, 34%);
    --ck-color-button-action-disabled-background: hsl(168, 76%, 42%);
    --ck-color-button-action-text: var(--ck-custom-white);

    --ck-color-button-save: hsl(120, 100%, 46%);
    --ck-color-button-cancel: hsl(15, 100%, 56%);

    /* -- Overrides the default .ck-dropdown class colors. -------------------------------------- */

    --ck-color-dropdown-panel-background: var(--ck-custom-background);
    --ck-color-dropdown-panel-border: var(--ck-custom-foreground);

    /* -- Overrides the default .ck-input class colors. ----------------------------------------- */

    --ck-color-input-background: var(--ck-custom-foreground);
    --ck-color-input-border: hsl(257, 3%, 43%);
    --ck-color-input-text: hsl(0, 0%, 98%);
    --ck-color-input-disabled-background: hsl(255, 4%, 21%);
    --ck-color-input-disabled-border: hsl(250, 3%, 38%);
    --ck-color-input-disabled-text: hsl(0, 0%, 46%);

    /* -- Overrides the default .ck-list class colors. ------------------------------------------ */

    --ck-color-list-background: var(--ck-custom-background);
    --ck-color-list-button-hover-background: var(--ck-color-base-foreground);
    --ck-color-list-button-on-background: var(--ck-color-base-active);
    --ck-color-list-button-on-background-focus: var(--ck-color-base-active-focus);
    --ck-color-list-button-on-text: var(--ck-color-base-background);

    /* -- Overrides the default .ck-balloon-panel class colors. --------------------------------- */

    --ck-color-panel-background: var(--ck-custom-background);
    --ck-color-panel-border: var(--ck-custom-border);

    /* -- Overrides the default .ck-toolbar class colors. --------------------------------------- */

    --ck-color-toolbar-background: var(--ck-custom-background);
    --ck-color-toolbar-border: var(--ck-custom-border);

    /* -- Overrides the default .ck-tooltip class colors. --------------------------------------- */

    --ck-color-tooltip-background: hsl(252, 7%, 14%);
    --ck-color-tooltip-text: hsl(0, 0%, 93%);

    /* -- Overrides the default colors used by the ckeditor5-image package. --------------------- */

    --ck-color-image-caption-background: hsl(0, 0%, 97%);
    --ck-color-image-caption-text: hsl(0, 0%, 20%);

    /* -- Overrides the default colors used by the ckeditor5-widget package. -------------------- */

    --ck-color-widget-blurred-border: hsl(0, 0%, 87%);
    --ck-color-widget-hover-border: hsl(43, 100%, 68%);
    --ck-color-widget-editable-focus-background: var(--ck-custom-background);

    /* -- Overrides the default colors used by the ckeditor5-link package. ---------------------- */

    --ck-color-link-default: hsl(190, 100%, 75%);
}
</style>